import React, { useState } from "react";
import { Link } from "react-router-dom";
import leadService from "../../services/leadService";

// Contacto de ventas del plan Enterprise ("A medida").
//
// Cinco campos: los tres primeros son lo mínimo para poder responder y los dos
// últimos califican sin fricción. "Egresos por mes" es el único campo de
// calificación que se justifica, porque es la variable con la que está armado
// el pricing: dice al toque si el prospecto es Enterprise o si le alcanza un
// plan autoservicio.

// Los tramos arrancan donde termina el autoservicio: Professional cubre hasta
// 3.299 egresos/mes y Enterprise empieza en 3.300. Sólo se subdivide de ahí para
// arriba, que es lo que califica el tamaño del deal. No hay opción por debajo a
// propósito: a este formulario se llega desde el CTA de Enterprise, y ofrecer un
// rango de plan autoservicio diluiría el sentido del tier.
//
// El campo es opcional: quien no conoce su volumen deja el selector sin elegir y
// el lead se guarda con egress_range en NULL.
const EGRESS_RANGES = [
  "3.300 a 5.000",
  "5.000 a 10.000",
  "10.000 a 15.000",
  "Más de 15.000",
];

const styles = {
  page: { backgroundColor: "#f5f5f7", minHeight: "100vh", padding: "40px 16px" },
  card: {
    maxWidth: 620,
    margin: "0 auto",
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: "44px 48px",
    boxShadow: "0 10px 30px rgba(22,28,45,.08)",
    color: "#161C2D",
    lineHeight: 1.6,
  },
  brandRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 },
  h1: { fontSize: "1.8rem", fontWeight: 800, margin: "0 0 6px" },
  lead: { color: "#4b5563", fontSize: "0.98rem", margin: "0 0 26px" },
  label: { display: "block", fontSize: "0.85rem", fontWeight: 600, margin: "0 0 6px", color: "#374151" },
  optional: { fontWeight: 400, color: "#9ca3af" },
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "11px 13px",
    fontSize: "0.95rem",
    fontFamily: "inherit",
    color: "#161C2D",
    border: "1px solid #d7dae0",
    borderRadius: 9,
    outline: "none",
    backgroundColor: "#fff",
  },
  field: { marginBottom: 16 },
  button: {
    width: "100%",
    padding: "13px 18px",
    fontSize: "1rem",
    fontWeight: 700,
    fontFamily: "inherit",
    color: "#fff",
    backgroundColor: "#2563EB",
    border: "none",
    borderRadius: 9,
    cursor: "pointer",
    marginTop: 8,
  },
  buttonBusy: { opacity: 0.65, cursor: "progress" },
  error: {
    margin: "0 0 18px",
    padding: "11px 14px",
    borderRadius: 9,
    backgroundColor: "#fef2f2",
    color: "#b91c1c",
    fontSize: "0.9rem",
  },
  link: { color: "#2563EB", textDecoration: "none", fontWeight: 600, fontSize: "0.9rem" },
  note: { color: "#6b7280", fontSize: "0.82rem", marginTop: 18, textAlign: "center" },
  okIcon: { fontSize: "2.4rem", lineHeight: 1, marginBottom: 10 },
  hp: { position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" },
};

const emptyForm = {
  name: "",
  email: "",
  company: "",
  phone: "",
  egress_range: "",
  website: "", // honeypot: invisible, sólo lo completa un bot
};

const ContactSalesPage = () => {
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;

    setError("");
    setSending(true);

    const { ok, error: err } = await leadService.create({
      ...form,
      plan: "Enterprise",
      source: "web:contacto-ventas",
    });

    setSending(false);
    if (!ok) {
      setError(err);
      return;
    }

    // El evento de conversión va acá, en el envío exitoso — no en el click del
    // botón de la landing, para no contar como lead a quien abandona el form.
    if (window.fbq) window.fbq("track", "Lead", { content_name: "Enterprise" });
    if (window.gtag) window.gtag("event", "generate_lead", { currency: "USD", value: 0 });

    setSent(true);
  };

  if (sent) {
    return (
      <div style={styles.page}>
        <div style={{ ...styles.card, textAlign: "center" }}>
          <div style={styles.okIcon}>✅</div>
          <h1 style={styles.h1}>¡Listo, recibimos tu consulta!</h1>
          <p style={styles.lead}>
            Te vamos a responder por email <strong>dentro de las próximas 24 horas hábiles</strong>.
            Si es urgente, escribinos a <a href="mailto:contact@voras.com" style={styles.link}>contact@voras.com</a>.
          </p>
          <Link to="/" style={styles.link}>← Volver al inicio</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.brandRow}>
          <h1 style={styles.h1}>Hablemos de tu operación</h1>
          <Link to="/" style={styles.link}>← Voras</Link>
        </div>
        <p style={styles.lead}>
          Contanos cuánto despachás y armamos un plan a tu medida. Te respondemos
          dentro de las 24 horas hábiles.
        </p>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={onSubmit} noValidate>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="name">Nombre y apellido</label>
            <input style={styles.input} id="name" name="name" value={form.name}
              onChange={onChange} required autoComplete="name" maxLength={120} />
          </div>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="email">Email</label>
            <input style={styles.input} id="email" name="email" type="email" value={form.email}
              onChange={onChange} required autoComplete="email" maxLength={180} />
          </div>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="company">Empresa</label>
            <input style={styles.input} id="company" name="company" value={form.company}
              onChange={onChange} required autoComplete="organization" maxLength={160} />
          </div>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="phone">
              Teléfono <span style={styles.optional}>(opcional)</span>
            </label>
            <input style={styles.input} id="phone" name="phone" value={form.phone}
              onChange={onChange} autoComplete="tel" maxLength={60} />
          </div>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="egress_range">
              Egresos por mes <span style={styles.optional}>(aproximado)</span>
            </label>
            <select style={styles.input} id="egress_range" name="egress_range"
              value={form.egress_range} onChange={onChange}>
              <option value="">Elegí una opción</option>
              {EGRESS_RANGES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          {/* Honeypot anti-bot: oculto para personas, invisible para lectores de pantalla */}
          <div style={styles.hp} aria-hidden="true">
            <label htmlFor="website">No completar</label>
            <input id="website" name="website" value={form.website} onChange={onChange} tabIndex={-1} autoComplete="off" />
          </div>

          <button type="submit" style={{ ...styles.button, ...(sending ? styles.buttonBusy : {}) }} disabled={sending}>
            {sending ? "Enviando…" : "Enviar consulta"}
          </button>
        </form>

        <p style={styles.note}>
          ¿Buscabas los planes autoservicio? <Link to="/#planes" style={styles.link}>Verlos acá</Link>
        </p>
      </div>
    </div>
  );
};

export default ContactSalesPage;
